import React, { createContext, useContext, useState, useEffect } from 'react';
import { Direction, LocaleMetadata, SUPPORTED_LOCALES } from './types';
import { translations } from './translations';
import { LocalizedText } from '../../types/profile';

interface LocalizationContextType {
  locale: string;
  direction: Direction;
  metadata: LocaleMetadata;
  supportedLocales: string[];
  setLocale: (newLocale: string) => void;
  t: (key: string, fallback?: string) => string;
  resolve: (textObj?: LocalizedText | string, fallback?: string) => string;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

interface LocalizationProviderProps {
  children: React.ReactNode;
  defaultLocale?: string;
  supportedLocales?: string[];
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({
  children,
  defaultLocale = 'ar',
  supportedLocales = ['ar', 'en'],
}) => {
  const [locale, setLocaleState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('sdp_locale');
      if (saved && supportedLocales.includes(saved)) {
        return saved;
      }
    } catch {
      // ignore
    }
    return defaultLocale;
  });

  const metadata: LocaleMetadata = SUPPORTED_LOCALES[locale] || {
    code: locale,
    name: locale,
    nativeName: locale,
    direction: locale === 'ar' ? 'rtl' : 'ltr',
  };

  const direction = metadata.direction;

  const setLocale = (newLocale: string) => {
    if (supportedLocales.includes(newLocale)) {
      setLocaleState(newLocale);
      try {
        localStorage.setItem('sdp_locale', newLocale);
      } catch {
        // ignore
      }
    }
  };

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    document.body.dir = direction;
    if (direction === 'rtl') {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [locale, direction]);

  const t = (key: string, fallback?: string): string => {
    const dict = translations[locale] || translations.en;
    if (dict && dict[key]) {
      return dict[key];
    }
    const enDict = translations.en;
    if (enDict && enDict[key]) {
      return enDict[key];
    }
    return fallback || key;
  };

  const resolve = (textObj?: LocalizedText | string, fallback = ''): string => {
    if (!textObj) return fallback;
    if (typeof textObj === 'string') return textObj;
    if (textObj[locale]) return textObj[locale];
    if (textObj.en) return textObj.en;
    if (textObj.ar) return textObj.ar;
    const firstVal = Object.values(textObj)[0];
    return firstVal || fallback;
  };

  return (
    <LocalizationContext.Provider
      value={{
        locale,
        direction,
        metadata,
        supportedLocales,
        setLocale,
        t,
        resolve,
      }}
    >
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};

